import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useSsnitDue } from '../api/hr';
import { useDaily } from '../api/queries';
import { useReorderList } from '../api/stock';
import { DEPARTMENT_LABEL, addDays, formatDate, formatGhs } from '../format';
import { Badge, Button, ErrorNote, Loading } from '../ui/kit';
import { PageHead } from './shared';

const METHOD_LABEL: Record<string, string> = { CASH: 'Cash', MOMO: 'Mobile money', CARD: 'Card', BANK_TRANSFER: 'Bank transfer', OTHER: 'Other' };

/** How a figure compares with the same weekday a week before: "up 12%", "down 5%" or "same". */
const versus = (now: number, then: number): { text: string; tone: 'good' | 'bad' | '' } => {
  if (then <= 0) return { text: now > 0 ? 'nothing the week before' : '', tone: '' };
  const pct = Math.round(((now - then) / then) * 100);
  return pct === 0 ? { text: 'same as a week before', tone: '' } : { text: `${pct > 0 ? 'up' : 'down'} ${Math.abs(pct)}% on a week before`, tone: pct > 0 ? 'good' : 'bad' };
};

/**
 * The owner's morning page: how yesterday went (or any day picked), set beside the same day a week earlier, and the
 * things that need doing. Built from the reports the hotel already has, so the numbers match everywhere else.
 */
export function DailySummary() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const [date, setDate] = useState(() => addDays(today, -1));
  const day = useDaily(date, true);
  const before = useDaily(addDays(date, -7), true);
  const reorder = useReorderList(can('stock.view'));
  const ssnit = useSsnitDue(can('payroll.view'));
  const toBuy = reorder.data?.items ?? [];
  const owed = ssnit.data ?? [];

  if (day.isLoading) return <Loading rows={4} />;
  if (day.error || !day.data) return <ErrorNote error={day.error} retry={() => void day.refetch()} />;
  const d = day.data;
  const w = before.data;
  const isToday = date >= today;
  const departments = Object.entries(d.revenueByDepartment).filter(([, v]) => v !== 0).sort((a, b) => b[1] - a[1]);
  const methods = Object.entries(d.paymentsReceivedByMethod).filter(([, v]) => v !== 0).sort((a, b) => b[1] - a[1]);
  const rev = w ? versus(d.totalRevenue, w.totalRevenue) : null;
  const occ = w ? versus(d.rooms.occupancyPct, w.rooms.occupancyPct) : null;

  return (
    <div className="page">
      <PageHead eyebrow={formatDate(date, true)} title={isToday ? 'Today so far' : 'How the day went'}>
        <Button className="no-print" onClick={() => window.print()}>Print</Button>
      </PageHead>

      <div className="row no-print" role="group" aria-label="Day">
        <Button onClick={() => setDate(addDays(date, -1))}>Earlier day</Button>
        <Button onClick={() => setDate(addDays(date, 1))} disabled={date >= today}>Later day</Button>
        <Button variant="ghost" onClick={() => setDate(addDays(today, -1))} disabled={date === addDays(today, -1)}>Yesterday</Button>
      </div>

      <section className="stats" aria-label="The day in numbers">
        <div className="card stat">
          <span className="eyebrow">Revenue</span>
          <span className="figure num">{formatGhs(d.totalRevenue)}</span>
          {rev?.text ? <span className="small muted">{rev.text}</span> : null}
        </div>
        <div className="card stat">
          <span className="eyebrow">Money received</span>
          <span className="figure num">{formatGhs(d.totalReceived)}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Rooms occupied</span>
          <span className="figure num">{d.rooms.occupancyPct}%</span>
          <span className="small muted">{d.rooms.inHouse} of {d.rooms.total}{occ?.text ? `, ${occ.text}` : ''}</span>
        </div>
        <div className="card stat">
          <span className="eyebrow">Arrived / left</span>
          <span className="figure num">{d.arrivals.length} / {d.departures.length}</span>
        </div>
      </section>

      <div className="grid-2">
        <section className="card stack" aria-label="Where the revenue came from">
          <h2 className="h2">Where it came from</h2>
          {departments.length === 0 ? <p className="muted">No sales recorded.</p> : (
            <dl className="kv">
              {departments.map(([k, v]) => (<Fragment key={k}><dt>{DEPARTMENT_LABEL[k] ?? k}</dt><dd className="num">{formatGhs(v)}</dd></Fragment>))}
            </dl>
          )}
        </section>
        <section className="card stack" aria-label="How guests paid">
          <h2 className="h2">How it was paid</h2>
          {methods.length === 0 ? <p className="muted">No payments recorded.</p> : (
            <dl className="kv">
              {methods.map(([k, v]) => (<Fragment key={k}><dt>{METHOD_LABEL[k] ?? k}</dt><dd className="num">{formatGhs(v)}</dd></Fragment>))}
            </dl>
          )}
        </section>
      </div>

      <section className="card stack" aria-label="Needs attention">
        <h2 className="h2">Needs attention</h2>
        {toBuy.length === 0 && owed.length === 0 ? <p className="muted">Nothing is waiting.</p> : null}
        {toBuy.length > 0 ? (
          <Link to="/stock?tab=buy" className="row" style={{ textDecoration: 'none', color: 'inherit', justifyContent: 'space-between' }}>
            <span>Stock to buy: {toBuy.filter((i) => i.state === 'OUT').length} out, {toBuy.filter((i) => i.state === 'LOW').length} running low</span>
            <Badge tone="warn">{toBuy.length}</Badge>
          </Link>
        ) : null}
        {owed.map((m) => (
          <Link key={m.runId} to={`/payroll/${m.runId}`} className="row" style={{ textDecoration: 'none', color: 'inherit', justifyContent: 'space-between' }}>
            <span>SSNIT for {m.period}: {formatGhs(m.outstanding)} {m.status === 'LATE' ? `late by ${m.daysLate} days` : m.daysUntilDue === 0 ? 'due today' : `due in ${m.daysUntilDue} days`}</span>
            <Badge tone={m.status === 'LATE' ? 'bad' : m.daysUntilDue <= 5 ? 'warn' : 'info'}>{m.status === 'LATE' ? 'Late' : 'Due'}</Badge>
          </Link>
        ))}
      </section>
    </div>
  );
}
